/**
 * Reusable fullscreen webcam capture modal.
 * Supports front/back camera toggle on mobile.
 */
import { useRef, useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, X, RotateCcw, AlertCircle, FlipHorizontal, SwitchCamera } from "lucide-react";
import { colors } from "@/theme";

export interface CapturedPhoto {
  blob: Blob;
  previewUrl: string;
}

interface WebcamCaptureProps {
  open: boolean;
  onCapture: (photo: CapturedPhoto) => void;
  onCancel: () => void;
  hint?: string;
  /** Which camera to start with. Default: "user" (front/selfie) */
  initialFacing?: "user" | "environment";
}

export default function WebcamCapture({
  open,
  onCapture,
  onCancel,
  hint = "",
  initialFacing,
}: WebcamCaptureProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const [stage, setStage] = useState<"camera" | "preview">("camera");
  const [preview, setPreview] = useState<string | null>(null);
  const [capturedBlob, setCapturedBlob] = useState<Blob | null>(null);
  const [cameraError, setCameraError] = useState("");
  const [facingMode, setFacingMode] = useState<"user" | "environment">(initialFacing);
  const [switching, setSwitching] = useState(false);
  const [hasMultipleCameras, setHasMultipleCameras] = useState(false);

  // Mirror only when using front camera
  const mirrored = facingMode === "user";

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  const startStream = useCallback(async (facing: "user" | "environment") => {
    setCameraError("");
    stopStream();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: facing, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch {
      setCameraError("Camera access denied. Please allow camera permission and try again.");
    }
  }, [stopStream]);

  // Detect how many video input devices exist
  useEffect(() => {
    navigator.mediaDevices.enumerateDevices().then((devices) => {
      const videoInputs = devices.filter((d) => d.kind === "videoinput");
      setHasMultipleCameras(videoInputs.length > 1);
    }).catch(() => setHasMultipleCameras(false));
  }, []);

  useEffect(() => {
  if (open) {
    const defaultFacing = initialFacing || "environment";

    setStage("camera");
    setPreview(null);
    setCapturedBlob(null);
    setFacingMode(defaultFacing);

    startStream(defaultFacing);
  } else {
    stopStream();
  }

  return () => stopStream();
}, [open, initialFacing]);// eslint-disable-line react-hooks/exhaustive-deps

  const handleFlip = useCallback(async () => {
    if (switching) return;
    setSwitching(true);
    const next = facingMode === "user" ? "environment" : "user";
    setFacingMode(next);
    await startStream(next);
    setSwitching(false);
  }, [facingMode, startStream, switching]);

  const handleCapture = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    // If front camera: flip canvas horizontally so saved image matches what user sees
    if (mirrored) {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(video, 0, 0);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        stopStream();
        setCapturedBlob(blob);
        setPreview(canvas.toDataURL("image/jpeg"));
        setStage("preview");
      },
      "image/jpeg",
      0.92
    );
  }, [mirrored, stopStream]);

  const handleRetake = useCallback(() => {
    setPreview(null);
    setCapturedBlob(null);
    setStage("camera");
    startStream(facingMode);
  }, [startStream, facingMode]);

  const handleConfirm = useCallback(() => {
    if (!capturedBlob || !preview) return;
    onCapture({ blob: capturedBlob, previewUrl: preview });
  }, [capturedBlob, preview, onCapture]);

  const handleCancel = useCallback(() => {
    stopStream();
    onCancel();
  }, [stopStream, onCancel]);

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="webcam-modal"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9999] bg-black flex flex-col"
      >
        {stage === "camera" ? (
          <>
            {/* Live video — mirrored only for front camera */}
            <video
              ref={videoRef}
              className={`absolute inset-0 w-full h-full object-cover ${mirrored ? "scale-x-[-1]" : ""}`}
              autoPlay
              playsInline
              muted
              disablePictureInPicture
              controlsList="nodownload nofullscreen noremoteplayback"
            />

            {/* Top bar */}
            <div className="absolute top-0 inset-x-0 z-10 flex items-center justify-between px-5 pt-12 pb-6 bg-gradient-to-b from-black/60 to-transparent">
              <button
                onClick={handleCancel}
                className="w-10 h-10 rounded-full bg-white/15 border border-white/20 flex items-center justify-center text-white hover:bg-white/25 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
              <p className="text-white/80 text-sm font-semibold">{hint}</p>
              {/* Flip camera — only shown when device has multiple cameras */}
              {hasMultipleCameras ? (
                <button
                  onClick={handleFlip}
                  disabled={switching}
                  className="w-10 h-10 rounded-full bg-white/15 border border-white/20 flex items-center justify-center text-white hover:bg-white/25 transition-colors disabled:opacity-40"
                >
                  <SwitchCamera className={`w-5 h-5 ${switching ? "animate-spin" : ""}`} />
                </button>
              ) : (
                <div className="w-10" />
              )}
            </div>

            {/* Camera error */}
            {cameraError && (
              <div className="absolute inset-x-4 top-28 z-20 bg-red-500/90 rounded-xl px-4 py-3 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-white mt-0.5 flex-shrink-0" />
                <p className="text-white text-sm">{cameraError}</p>
              </div>
            )}

            {/* Bottom capture zone */}
            <div className="absolute bottom-0 inset-x-0 z-10 flex flex-col items-center gap-3 pb-12 pt-6 bg-gradient-to-t from-black/70 to-transparent">
              <button
                onClick={handleCapture}
                className="w-16 h-16 rounded-full border-4 border-white flex items-center justify-center transition-transform active:scale-95 hover:scale-105 shadow-xl"
                style={{ background: colors.brand.gradient }}
              >
                <Camera className="w-7 h-7 text-white" />
              </button>
              <p className="text-white/50 text-xs">
                 Tap to capture
              </p>
            </div>

            <canvas ref={canvasRef} className="hidden" />
          </>
        ) : (
          /* Preview stage — image is already correctly oriented from canvas */
          <>
            {preview && (
              <img
                src={preview}
                alt="Preview"
                className="absolute inset-0 w-full h-full object-cover"
              />
            )}

            {/* Top overlay */}
            <div className="absolute top-0 inset-x-0 z-10 h-24 bg-gradient-to-b from-black/60 to-transparent flex items-start justify-between px-5 pt-5">
              <button
                onClick={handleCancel}
                className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center text-white"
              >
                <X className="w-5 h-5" />
              </button>
              <p className="text-white/70 text-sm font-semibold pt-2">Preview</p>
              <div className="w-10" />
            </div>

            {/* Bottom actions */}
            <motion.div
              initial={{ y: 80, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              className="absolute bottom-0 inset-x-0 z-10 px-5 py-7 rounded-t-3xl"
              style={{ background: "rgba(8,5,30,0.96)" }}
            >
              <p className="text-white text-xl font-extrabold text-center mb-1">Use this photo?</p>
              <p className="text-white/45 text-sm text-center mb-5">Make sure the image is clear and well-lit.</p>
              <div className="flex gap-3">
                <button
                  onClick={handleRetake}
                  className="flex-1 py-4 rounded-xl border border-white/20 text-white font-bold text-sm flex items-center justify-center gap-2 hover:bg-white/10 transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                  Retake
                </button>
                <button
                  onClick={handleConfirm}
                  className="flex-1 py-4 rounded-xl font-bold text-white text-sm flex items-center justify-center gap-2"
                  style={{ background: colors.brand.gradient }}
                >
                  <Camera className="w-4 h-4" />
                  Use Photo
                </button>
              </div>
            </motion.div>
          </>
        )}
      </motion.div>
    </AnimatePresence>
  );
}
